import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Tag, CalendarClock, ChevronDown, PlusCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { resolveAccent, tint } from '../../utils/accentColor';
import { getIcon } from '../../services/iconRegistry';
import {
  billingApi, payableMonthlyCents, overallDiscountUnverified,
  type PlanComposition, type SoldLocation,
} from '../../services/billingApi';
import { dealsApi } from '../../services/dealsApi';
import { api } from '../../services/apiService';
import type { DeviceUnitRow } from '../../services/hardwareApi';
import { useDataVersion } from '../../services/dataChangeBus';
import { RubyLoader } from '../../components/RubyLoader';

/**
 * WHAT THEY SIGNED UP FOR, and WHAT IT COSTS THEM NEXT.
 *
 * Two small panels that read the same two sources — the active plan assignment
 * and the device agreements already on this order's units — rather than
 * recomputing a price. Nothing here decides money; it reports what billing
 * decided, which is the only way a screen and an invoice stay in agreement.
 */

const money = (cents?: number) =>
  typeof cents === 'number'
    ? `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '—';

/**
 * ===========================================================================
 * THE SOFTWARE HALF OF THE BILL, on its own
 * ===========================================================================
 *
 * The order header needs one number — what the tenant pays for software each
 * month — to add to the hardware and call the sum a subscription. Getting it
 * is the same two hops `PlanPanel` below makes (the stored assignment, then the
 * server's composition of it), and it is exported as a hook rather than copied
 * so a screen quoting a total and the panel quoting its parts cannot end up
 * reading different sources.
 *
 * `null` MEANS "WE DO NOT KNOW", and it is returned rather than 0 in every case
 * that is not a confirmed figure: no active assignment, a request that failed,
 * or an overall discount the server has not confirmed is already applied. That
 * last one is the reason `PlanPanel` refuses to print a total at all — both
 * readings of `netCents` are wrong in a different direction — and a header that
 * quietly showed 0 there would state a subscription price that is not the
 * customer's. Callers must show the hardware alone rather than invent a total.
 */
export function useSoftwareMonthlyCents(tenantId: string | undefined, version?: number): number | null {
  const [cents, setCents] = useState<number | null>(null);
  /* Which tenant `cents` belongs to. A re-read of the SAME tenant (a `version`
     bump after a save) keeps the figure on screen while it fetches — no flicker
     to "unknown" and back. Only a different tenant clears it first. */
  const forTenant = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!tenantId) { forTenant.current = undefined; setCents(null); return undefined; }
    let cancelled = false;
    if (forTenant.current !== tenantId) { forTenant.current = tenantId; setCents(null); }
    (async () => {
      const assignment: any = await billingApi.getActivePlanAssignment(tenantId).catch(() => undefined);
      if (cancelled || assignment === undefined) return; // failed read — keep the last figure
      const row = assignment?.items?.[0];
      if (!row?.assignmentId) { setCents(null); return; }
      const comp = await billingApi
        .previewPlanAssignment(tenantId, { assignmentId: row.assignmentId })
        .catch(() => undefined);
      if (cancelled || comp === undefined) return; // failed read — keep the last figure
      setCents(!comp || overallDiscountUnverified(comp) ? null : payableMonthlyCents(comp));
    })();
    return () => { cancelled = true; };
  }, [tenantId, version]);

  return cents;
}

/**
 * THE SAME FIGURE FOR A LIST OF TENANTS — the orders screen, which quotes a
 * subscription per row.
 *
 * Two requests per tenant, so it is deliberately bounded: DISTINCT tenant ids
 * only (an operator's forty orders are rarely forty customers), a small
 * concurrency window rather than a burst, and an empty answer for a tenant it
 * could not price. It never blocks the rows — orders render immediately and
 * the money fills in, because the pipeline is the job and the price is
 * information.
 *
 * A tenant ABSENT from the returned map means "not known", exactly as `null`
 * does in the single-tenant hook above, and callers must show the hardware
 * alone rather than adding to a zero.
 */
const SOFTWARE_FETCH_WINDOW = 4;

export function useSoftwareMonthlyByTenant(tenantIds: string[]): Record<string, number> {
  const [byTenant, setByTenant] = useState<Record<string, number>>({});
  // A stable key, so a re-render that produces an equal-but-new array does not
  // refetch the whole list.
  const key = Array.from(new Set(tenantIds.filter(Boolean))).sort().join(',');

  useEffect(() => {
    const ids = key ? key.split(',') : [];
    if (ids.length === 0) { setByTenant({}); return undefined; }
    let cancelled = false;

    (async () => {
      const found: Record<string, number> = {};
      for (let i = 0; i < ids.length; i += SOFTWARE_FETCH_WINDOW) {
        if (cancelled) return;
        await Promise.all(ids.slice(i, i + SOFTWARE_FETCH_WINDOW).map(async (tenantId) => {
          const assignment: any = await billingApi.getActivePlanAssignment(tenantId).catch(() => null);
          const row = assignment?.items?.[0];
          if (!row?.assignmentId) return;
          const comp = await billingApi
            .previewPlanAssignment(tenantId, { assignmentId: row.assignmentId })
            .catch(() => null);
          // An unverified overall discount makes `netCents` ambiguous — see
          // `useSoftwareMonthlyCents`. Left out of the map, never zeroed.
          if (!comp || overallDiscountUnverified(comp)) return;
          found[tenantId] = payableMonthlyCents(comp);
        }));
        // Paint each window as it lands rather than holding the whole list
        // back for the slowest tenant.
        if (!cancelled) setByTenant({ ...found });
      }
    })();

    return () => { cancelled = true; };
  }, [key]);

  return byTenant;
}

/* ── the package, from the deal they signed up on ──────────────────────── */

/**
 * WHAT THEY BOUGHT, AND HOW WE ARRIVED AT THE PRICE.
 *
 * The same card the customer was sold on — the dark landing-page price card
 * from `DealLandingPreview`, laid on its side. Showing the operator a
 * different-looking thing than the customer saw makes "is this what they
 * agreed to?" a question nobody can answer at a glance.
 *
 * Packages render in the colour and icon their OWN record carries, so a
 * package looks the same here, on the landing page and in the packages
 * catalogue. `resolveAccent` handles an arbitrary operator-chosen colour
 * without assuming it is dark.
 *
 * And it answers the question the price raises: WHO SOLD THIS. A deal code
 * belongs to a partner who is owed commission on it; a sale with no code is a
 * direct sale and owes nobody. Both are said out loud, because a blank row and
 * an unattributed sale look identical — and one of those is a partner not
 * getting paid.
 */

const RUBY = '#DC143C';
const RUBY_DEEP = '#9F1239';

const money2 = (cents?: number) => (typeof cents === 'number' ? `$${(cents / 100).toFixed(2)}` : '—');

interface PkgRow {
  id: string; name: string; basePrice?: number; isCore?: boolean;
  icon?: string; colorType?: 'solid' | 'gradient'; color?: string;
  gradientFrom?: string; gradientTo?: string;
}

export const PlanPanel: React.FC<{
  tenantId: string;
  /**
   * The ORDER'S OWN attribution snapshot, taken at checkout. Preferred over
   * anything read from the tenant: the tenant's `dealCode` is rewritten by a
   * later plan change, which would re-attribute this order to whoever sold the
   * customer their newest plan.
   */
  orderDealCode?: string;
  orderAffiliateId?: string;
  dealCode?: string;
}> = ({ tenantId, orderDealCode, orderAffiliateId, dealCode }) => {
  const [plan, setPlan] = useState<any>(null);
  const [comp, setComp] = useState<PlanComposition | null>(null);
  const [pkgs, setPkgs] = useState<Record<string, PkgRow>>({});
  const [partner, setPartner] = useState<any>(null);
  const [dealName, setDealName] = useState<string | null>(null);
  const [resolvedCode, setResolvedCode] = useState<string | null>(null);
  /* did the code name a real deal? absent partner means different things
     depending on the answer */
  const [dealFound, setDealFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);
  const dataVersion = useDataVersion();
  /**
   * THE SOFTWARE SIDE OF THE OFFER, from the two records that describe this
   * sale — the completion's own lines and the deal version the tenant SIGNED.
   *
   * The strikethrough and the savings badge here have always been driven by the
   * plan assignment's composition, which carries a permanent deal discount and
   * nothing else. A tenant with no assignment — this one has none — got "no
   * active plan", and a tenant whose offer is a limited-time PROMOTION got a
   * full-price banner beside a card being charged less. Software only: hardware
   * is priced per device on its own terms and is stated by the schedule below.
   */
  const [offer, setOffer] = useState<{
    listCents: number;
    netCents: number;
    savedCents: number;
    /** Shortest promotion running — the first date this saving changes. */
    months?: number;
    /** The deal's own packages at list, and what the deal sells them for. */
    dealListCents: number;
    dealPriceCents?: number;
    dealCount: number;
    /** Bought on top of the offer, at list. */
    addOnCents: number;
    addOnCount: number;
    /** Which package ids the offer covers — for the per-row badges. */
    dealPackageIds: Set<string>;
    /** Every promotion running, summed. */
    promoOffCents: number;
    promoCount: number;
    /**
     * WHAT WE ACTUALLY SENT, and what the deal says we should have.
     *
     * They differ on any order completed before the 2026-09-06 ruling, which
     * discarded a deal's bundle price the moment anything was added. The panel
     * states BOTH rather than quietly printing the intended one: an operator
     * reading this is checking a real charge, and showing them the price that
     * was meant to apply as though it did is how the fault stayed hidden.
     */
    settledActualCents: number;
    settledIntendedCents?: number;
    /** The version that sold it, so the note names the contract it is quoting. */
    dealVersionLabel?: number;
  } | null>(null);

  const load = useCallback(async () => {
    const [assignment, list, tenantPkgs] = await Promise.all([
      billingApi.getActivePlanAssignment(tenantId).catch(() => null),
      /*
        PACKAGES, not billing SKUs. `planPackageIds` are package ids (`pkg_…`);
        looking them up in the billing catalogue resolved nothing and the stage
        rendered twenty raw ids, which tells a reader less than showing nothing.
      */
      api.packages.listVisible().catch(() => ({ packages: [] as any[] })),
      /*
        THE DEAL CODE, FROM THE TENANT RECORD ITSELF.

        It used to arrive as a prop read off `api.tenants.getMetadata`, which
        builds a whitelisted BusinessProfile — `dealCode` is not one of the
        fields it copies, so the prop was structurally always undefined and
        every sale read as direct. `getTenantPackages` returns the tenant row
        that actually carries it.
      */
      (api as any).superAdmin.getTenantPackages(tenantId).catch(() => null),
    ]);
    const tenantDealCode = (tenantPkgs as any)?.tenant?.dealCode;
    const row = (assignment as any)?.items?.[0] || null;
    setPlan(row);

    /*
      THE MONEY COMES FROM THE SERVER COMPOSING THE PLAN, not from the
      assignment row.

      `PlanAssignmentRow` carries `planPackageIds` and `discounts` and NO
      amounts at all — the previous code read `totals.netCents ?? lineNetCents`
      off it, fields that have never existed there, and hid the result whenever
      it came back undefined, which was always. Composing the stored assignment
      is how the current bill is meant to be read: one server-side function
      prices it, so this screen and the invoice cannot disagree.
    */
    if (row?.assignmentId) {
      const c = await billingApi
        .previewPlanAssignment(tenantId, { assignmentId: row.assignmentId })
        .catch(() => null);
      setComp(c);
    }
    setPkgs(Object.fromEntries(
      (list.packages || []).map((p: any) => [p.id || p.packageId, { ...p, id: p.id || p.packageId }]),
    ));

    /*
      THE OFFER ON THE SOFTWARE, from the sale itself.

      The completion's `lines` are what we sent Stripe, per SKU; the PINNED deal
      version carries the promotion applied to each package. Joined through the
      billing SKU's own `packageId`, because `packageIntros` is keyed by package.

      Every read is best-effort and the panel is unchanged without them: a
      missing offer understates a discount, a wrong one misstates a bill.
    */
    try {
      const [sold, skus] = await Promise.all([
        billingApi.getOnboardingResult(tenantId).catch(() => null),
        billingApi.listSkus().catch(() => null),
      ]);
      const soldLines = ((sold as any)?.result?.locations || [])
        .flatMap((l: any) => l.lines || [])
        .filter((l: any) => l.kind !== 'device');
      const packageIdBySku: Record<string, string> = {};
      for (const s of (skus as any)?.items || []) if (s.packageId) packageIdBySku[s.skuId] = s.packageId;

      /*
        THE TENANT ROW, NOT `getTenantPackages`'s SUMMARY.

        That endpoint returns a WHITELISTED tenant — `id, name, productType,
        enabledPackages, legacyPlan, dealCode` — and nothing else. `dealId` and
        `dealVersionNo` are not on the list, so reading them there yielded
        `undefined`, the deal version was never fetched, every line counted as
        in-deal, and the whole breakdown silently hid itself behind its own
        guard.

        This is the THIRD time this exact trap has been hit in this file: the
        notes above record `dealCode` being read off `getMetadata`'s whitelist
        and being structurally undefined, and the payment panel records
        `stripeCustomerId` the same way. A whitelisted summary is not the row.
      */
      const tenantRow: any = await api.superAdmin.getTenant(tenantId).catch(() => null);
      const dealId = tenantRow?.dealId;
      const versionNo = tenantRow?.dealVersionNo;
      let intros: Record<string, { introPriceCents: number; introMonths: number }> = {};
      let dealPackageIdList: string[] = [];
      let dealPrice: number | undefined;
      if (dealId && typeof versionNo === 'number') {
        const v: any = await dealsApi.getVersion(dealId, versionNo).catch(() => null);
        const version = v?.version || v;
        intros = version?.packageIntros || {};
        dealPackageIdList = version?.packageIds || [];
        // What the offer SELLS its package set for — the figure the bundle
        // price ruling is about.
        dealPrice = typeof version?.priceCents === 'number' ? version.priceCents : undefined;
      }

      if (soldLines.length > 0) {
        /*
          IN THE DEAL vs BOUGHT ON TOP.

          Ruling 2026-09-06: the deal's price holds for the deal's own packages
          and anything added prices at list. So a total is two different kinds
          of money, and "$165.91 software" answers none of the questions an
          operator has about it. The deal's `packageIds` is the whole
          definition — a line whose package is not in it was an add-on.
        */
        const dealPackageIds = new Set<string>(dealPackageIdList);
        let listCents = 0;
        let dealListCents = 0;
        let dealCount = 0;
        let addOnCents = 0;
        let addOnCount = 0;
        let promoOffCents = 0;
        let promoCount = 0;
        const runningMonths: number[] = [];
        for (const l of soldLines) {
          const qty = l.quantity || 1;
          const packageId = packageIdBySku[l.skuId] || '';
          listCents += l.amountCents * qty;

          // A promotion is a saving off whatever this line settles at, and it
          // is counted ONCE here rather than folded into two different totals.
          const intro = intros[packageId];
          if (intro && intro.introPriceCents < l.amountCents) {
            promoOffCents += (l.amountCents - intro.introPriceCents) * qty;
            promoCount += 1;
            runningMonths.push(intro.introMonths);
          }

          if (dealPackageIds.size > 0 && !dealPackageIds.has(packageId)) {
            addOnCents += l.amountCents * qty;
            addOnCount += 1;
          } else {
            dealListCents += l.amountCents * qty;
            dealCount += 1;
          }
        }

        /*
          THE SETTLED PRICE, TWICE — what we sent, and what the deal says.

          `listCents` is the sum of the lines we actually sent Stripe.
          `dealPrice + addOnCents` is what the offer promises. On an order
          completed before the 2026-09-06 ruling they differ by the bundle
          discount that was discarded, and the panel shows both: an operator is
          checking a REAL charge, and printing the intended figure as though it
          applied is how this stayed hidden for a month.
        */
        const settledIntendedCents = typeof dealPrice === 'number' && dealPackageIds.size > 0
          ? dealPrice + addOnCents
          : undefined;

        setOffer({
          listCents,
          // What is billed NOW: the settled lines we sent, less the promotions
          // running on them. Every row below subtracts from this same figure,
          // so the arithmetic on screen adds up.
          netCents: listCents - promoOffCents,
          savedCents: promoOffCents,
          dealListCents,
          dealPriceCents: dealPrice,
          dealCount,
          addOnCents,
          addOnCount,
          dealPackageIds,
          promoOffCents,
          promoCount,
          settledActualCents: listCents,
          settledIntendedCents,
          dealVersionLabel: typeof versionNo === 'number' ? versionNo : undefined,
          // The SHORTEST promotion: the first date this saving changes, which is
          // the one worth stating. Saying "12 months" while one of two offers
          // expires at three would be the more misleading half of the truth.
          months: runningMonths.length ? Math.min(...runningMonths) : undefined,
        });
      }
    } catch (e: any) {
      /*
        The panel stands without the breakdown — but NOT silently.

        A bare `catch {}` here swallowed the reason this block never rendered
        and cost a round of "it still shows the same". A degraded panel is
        acceptable; a degraded panel that cannot say why is not.
      */
      console.warn('[PlanPanel] offer breakdown unavailable:', e?.message || e);
    }

    /*
      THE PARTNER BEHIND THE CODE, resolved in two hops the platform already
      has: the code names a deal, the deal names an affiliate. Both are
      best-effort — a price with an unresolvable code is still worth showing,
      and failing the whole panel over a partner lookup would hide the plan.
    */
    /* the order's frozen code wins; the tenant is only a fallback for orders
       written before the snapshot existed */
    const code = orderDealCode || row?.dealCode || dealCode || tenantDealCode;
    setResolvedCode(code || null);
    if (code) {
      const d = await api.deals.getDealByCode(code).catch(() => null);
      if (d?.deal) setDealFound(true);
      if (d?.deal?.name) setDealName(d.deal.name);
      /* the affiliate frozen on the order beats the one the code resolves to
         today — a deal can be reassigned to a different partner */
      const affId = orderAffiliateId || d?.affiliateId;
      if (affId) {
        const a = await (api as any).affiliates?.admin?.getAffiliate?.(affId).catch(() => null);
        setPartner(a?.affiliate || a || null);
      }
    }
    setLoading(false);
  // dataVersion: re-read in place after a save anywhere on the order screen.
  }, [tenantId, dealCode, orderDealCode, orderAffiliateId, dataVersion]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <RubyLoader variant="inline" label="reading the plan…" />;
  }
  /*
    NO ASSIGNMENT IS NOT NO SALE.

    A plan assignment is an operator artefact; a tenant onboarded through the
    wizard may have none at all — this one does not — while paying for nine
    packages every month. Refusing to render on its absence told an operator
    "no active plan" about a customer whose card had just been charged. The
    completion's own lines answer the same question and are the record the
    money was taken against, so the panel stands on either.
  */
  if (!plan && !offer) {
    return <p className="text-[12px] text-stone-500">No plan or completed sale on this tenant yet.</p>;
  }

  /* `plan.dealCode` is not a field on PlanAssignmentRow — the prop from the
     tenant record is the real source; the row is read first only in case a
     future assignment carries one. */
  const code = resolvedCode || plan?.dealCode || dealCode;
  const ids: string[] = plan?.planPackageIds || [];

  /*
    ONE COMPOSED LINE PER PACKAGE, looked up — never re-added here. The server
    apportions an overall discount across the lines, so a package's real worth
    is its own `netCents` and nothing this screen could recompute.
  */
  const composed = new Map<string, { listCents: number; netCents: number }>();
  for (const loc of comp?.perLocation || []) {
    for (const it of loc.items) {
      const prev = composed.get(it.packageId);
      composed.set(it.packageId, {
        listCents: (prev?.listCents || 0) + it.listCents,
        netCents: (prev?.netCents || 0) + it.netCents,
      });
    }
  }

  const rows = ids.map((id) => ({
    ...(pkgs[id] || { id, name: comp?.packageNames?.[id] || id }),
    id,
    money: composed.get(id),
  }));

  /*
    REFUSE TO SHOW A TOTAL the server has not confirmed. When an overall
    discount is present but unconfirmed, both readings of `netCents` are wrong
    in a different direction — overstating the bill, or double-discounting it —
    and guessing about money is worse than saying we cannot price it.
  */
  const ambiguous = comp ? overallDiscountUnverified(comp) : false;
  const listCents = comp?.totals.listCents;
  const netCents = comp && !ambiguous ? payableMonthlyCents(comp) : undefined;
  const saved = comp && !ambiguous ? comp.totals.discountCents : 0;

  /*
    THE SALE'S OWN FIGURES WIN over the plan assignment's composition.

    The assignment prices what this tenant is entitled to TODAY and knows only a
    permanent discount. `offer` is what we actually sent Stripe for this order
    with the signed deal's promotion applied, so it is the one that matches the
    invoice — and it exists for accounts that have no assignment at all, where
    this banner previously said "no active plan" about a paying customer.
  */
  const showList = offer ? offer.listCents : listCents;
  const showNet = offer ? offer.netCents : netCents;
  const showSaved = offer ? offer.savedCents : saved;
  const showAmbiguous = offer ? false : ambiguous;
  /** A limited-time promotion is amber; a permanent deal discount stays green. */
  const promotional = !!offer?.months;

  return (
    <div
      className="rounded-[15px] overflow-hidden border border-white/[.07] text-[#e4e4e7]"
      style={{
        background: 'linear-gradient(180deg,rgba(220,20,60,.07),rgba(255,255,255,.035) 40%),'
          + 'radial-gradient(60% 40% at 70% 0%,rgba(220,20,60,.10),transparent 65%),#050507',
      }}
    >
      {/* the banner. Collapsed, this is all there is. */}
      <button onClick={() => setOpen((o) => !o)} className="w-full text-left px-[18px] py-[15px] flex items-center gap-4 flex-wrap">
        <span className="min-w-0">
          <span className="block text-[15.5px] font-extrabold text-white tracking-[-.01em]">
            {plan?.planName || dealName || 'Plan'}
          </span>
          <span className="block text-[11px] font-mono text-[#6b6b74] mt-1">
            {rows.length} package{rows.length === 1 ? '' : 's'} · software
          </span>
          {/*
            WHO SOLD THIS, on the band itself. It was only in the expanded
            detail, which meant the first question a reader has about a price —
            where did it come from — was the one thing they had to go looking
            for. A direct sale says so explicitly: a blank space and an
            unattributed sale look identical, and one of those is a partner
            not being paid.
          */}
          <span className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-[3px] rounded-full text-[10.5px] font-extrabold border"
            style={code
              ? { background: 'rgba(220,20,60,.14)', borderColor: 'rgba(220,20,60,.3)', color: '#fda4af' }
              : { background: 'rgba(255,255,255,.05)', borderColor: 'rgba(255,255,255,.12)', color: '#a1a1aa' }}>
            {code ? <><Tag size={10} /> {code}{partner ? ` · ${partner.name}` : ''}</> : 'Direct sale · no partner'}
          </span>
        </span>
        <span className="ml-auto flex items-baseline gap-2.5">
          {/*
            THE OFFER, ON THE BAND — struck list price, what it actually bills,
            and what that saves a month.

            AMBER when the saving is a limited-time promotion, green when it is
            the deal's permanent price. The two are different promises and the
            colour is what separates them: an amber figure is one that will go
            up, on a date this badge names, and an operator reading a green one
            should be able to trust it stays.
          */}
          {showSaved > 0 && (
            <span className={`text-[17px] font-light line-through ${promotional ? 'text-amber-200/45' : 'text-[#6b6b74]'}`}>
              {money2(showList)}
            </span>
          )}
          {showAmbiguous
            ? <span className="text-[13px] font-bold text-amber-400">cannot be priced</span>
            : (
              <span className={`text-[27px] font-extrabold tracking-[-.02em] tabular-nums ${
                promotional ? 'text-amber-300' : 'text-white'
              }`}>
                {money2(showNet)}
              </span>
            )}
          {!showAmbiguous && <span className="text-[11.5px] text-[#a1a1aa]">/mo</span>}
          {showSaved > 0 && (
            <span className="px-2.5 py-[3px] rounded-full text-[10.5px] font-extrabold border"
              style={promotional
                ? { background: 'rgba(217,119,6,.16)', color: '#fbbf24', borderColor: 'rgba(251,191,36,.32)' }
                : { background: 'rgba(74,222,128,.14)', color: '#4ade80', borderColor: 'rgba(74,222,128,.28)' }}>
              save {money2(showSaved)}/mo
              {offer?.months ? ` · ${offer.months} mo` : ''}
            </span>
          )}
          <ChevronDown size={15} className={`text-[#6b6b74] transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {open && (
        <div className="border-t border-white/[.08] px-[18px] pt-4 pb-[18px]">
          {/*
            HOW THE SOFTWARE FIGURE IS MADE UP — the deal, then what was added.

            Ruling 2026-09-06: a deal's price holds for its own packages and
            anything bought on top prices at list. So the monthly is two kinds
            of money and the arithmetic is written out, because "$165.91" alone
            answers none of the questions asked about it — and because the gap
            between the deal's list and its price is the discount the customer
            was promised, which is the first thing to check when a figure looks
            wrong.
          */}
          {offer && (offer.addOnCount > 0 || typeof offer.dealPriceCents === 'number') && (
            <div className="mb-3 rounded-[10px] border border-white/[.07] bg-white/[.025] px-3 py-2.5 space-y-1">
              {/*
                EVERY ROW SUBTRACTS FROM THE ONE TOTAL BELOW IT.

                The first version of this block mixed two different sums — a
                deal price that was never applied, beside a total computed from
                list less promotions — so the rows did not add up to the figure
                they sat under. That is the fault this panel exists to catch,
                produced by the panel itself.
              */}
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-[11.5px] text-[#a1a1aa] inline-flex items-center gap-1.5">
                  <Tag size={10} className="text-emerald-400/80" />
                  In the deal, at list
                  <span className="text-[#6b6b74]">· {offer.dealCount} package{offer.dealCount === 1 ? '' : 's'}</span>
                </span>
                <span className="text-[12px] text-[#e4e4e7] tabular-nums">{money2(offer.dealListCents)}</span>
              </div>

              {offer.addOnCount > 0 && (
                /* AMBER, and a plus. An add-on is the line that explains an
                   unexpected total, so it must be the line the eye lands on —
                   not another grey row among nine. */
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-[11.5px] text-amber-300 inline-flex items-center gap-1.5">
                    <PlusCircle size={10} />
                    Add-ons at list
                    <span className="text-amber-300/55">· {offer.addOnCount} outside the deal</span>
                  </span>
                  <span className="text-[12px] font-semibold text-amber-300 tabular-nums">+ {money2(offer.addOnCents)}</span>
                </div>
              )}

              {offer.promoCount > 0 && (
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-[11.5px] text-emerald-400">
                    Promotions
                    <span className="text-emerald-400/60">
                      {' · '}{offer.promoCount} package{offer.promoCount === 1 ? '' : 's'}
                      {offer.months ? `, ${offer.months} month${offer.months === 1 ? '' : 's'}` : ''}
                    </span>
                  </span>
                  <span className="text-[12px] font-semibold text-emerald-400 tabular-nums">
                    − {money2(offer.promoOffCents)}
                  </span>
                </div>
              )}

              <div className="flex items-baseline justify-between gap-3 pt-1.5 border-t border-white/[.08]">
                <span className="text-[11.5px] font-bold text-white">
                  Software, billing now
                  {offer.months ? <span className="text-[#6b6b74] font-normal"> · first {offer.months} months</span> : null}
                </span>
                <span className="text-[12.5px] font-extrabold text-white tabular-nums">{money2(offer.netCents)}</span>
              </div>
              {offer.promoOffCents > 0 && (
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-[11.5px] text-[#a1a1aa]">
                    Then, once the promotions end
                  </span>
                  <span className="text-[12px] text-[#e4e4e7] tabular-nums">{money2(offer.settledActualCents)}</span>
                </div>
              )}

              {/*
                THE BUNDLE DISCOUNT, WHEN IT WAS NOT APPLIED.

                Ruling 2026-09-06 fixed this forward, so an order completed
                before it still bills the deal's packages at LIST. Saying so is
                the whole point: printing the deal price as though it applied is
                exactly how $9.99 a month went unnoticed. New orders reconcile
                and this row never appears.
              */}
              {typeof offer.settledIntendedCents === 'number'
                && offer.settledIntendedCents !== offer.settledActualCents && (
                <div className="mt-1.5 pt-1.5 border-t border-amber-400/20 flex items-start gap-1.5">
                  <span className="text-[11px] text-amber-300/90 leading-relaxed">
                    Deal v{offer.dealVersionLabel} sells these {offer.dealCount} packages for{' '}
                    <b>{money2(offer.dealPriceCents)}</b>, so this order should settle at{' '}
                    <b>{money2(offer.settledIntendedCents)}</b> — it settles at{' '}
                    <b>{money2(offer.settledActualCents)}</b>. The bundle price was dropped because a
                    package outside the deal was added, which was the rule until 2026-09-06.
                    Existing subscriptions were not repriced.
                  </span>
                </div>
              )}
            </div>
          )}

          <p className="text-[9.5px] font-mono uppercase tracking-[0.16em] font-extrabold text-[#6b6b74] mb-2.5">
            What is in the plan · {rows.length} package{rows.length === 1 ? '' : 's'}
          </p>

          {rows.map((r) => {
            const a = resolveAccent(r);
            const Icon = getIcon(r.icon || '');
            return (
              <div key={r.id} className="flex items-center gap-2.5 px-2.5 py-[7px] rounded-[10px] mb-[5px] bg-white/[.025] border border-white/[.05]">
                <span className="flex-none w-[17px] h-[17px] rounded-full grid place-items-center text-[10px]"
                  style={{ background: 'rgba(220,20,60,.14)', color: '#fb7185' }}>&#10003;</span>
                {/* the package's OWN colour and icon */}
                <span className="flex-none w-5 h-5 rounded-md grid place-items-center"
                  style={{ background: tint(a.from, 0.18), color: a.from }}>
                  <Icon size={11} />
                </span>
                <span className="text-[12.5px] font-semibold text-[#d4d4d8] min-w-0 truncate">{r.name}</span>
                {/*
                  WHICH PACKAGE IS THE ADD-ON, on the package itself.

                  The totals above say one of nine is outside the deal; only
                  this row says WHICH. Drawn only for the add-on — badging all
                  nine would be nine badges to read instead of one to spot — and
                  only when the deal actually named a package set, because an
                  unknown must never be rendered as "not in the deal".
                */}
                {offer && offer.dealPackageIds.size > 0 && !offer.dealPackageIds.has(r.id) && (
                  <span className="flex-none inline-flex items-center gap-1 text-[9px] font-extrabold uppercase tracking-wider px-1.5 py-px rounded-full"
                    style={{ background: 'rgba(217,119,6,.18)', color: '#fbbf24', border: '1px solid rgba(251,191,36,.32)' }}>
                    <PlusCircle size={9} strokeWidth={3} /> add-on
                  </span>
                )}
                <span className="ml-auto flex-none tabular-nums flex items-baseline gap-1.5">
                  {r.money
                    ? (
                      <>
                        {r.money.listCents > r.money.netCents && (
                          <span className="text-[10.5px] text-[#6b6b74] line-through">{money2(r.money.listCents)}</span>
                        )}
                        <span className="text-[13px] font-extrabold text-white">{money2(r.money.netCents)}</span>
                      </>
                    )
                    : r.isCore
                      ? <span className="text-[10px] font-extrabold px-[7px] py-[2px] rounded-md" style={{ background: 'rgba(74,222,128,.12)', color: '#4ade80' }}>included</span>
                      : <span className="text-[11px] text-[#6b6b74]">not priced</span>}
                </span>
              </div>
            );
          })}

          {ambiguous && (
            <p className="text-[11.5px] text-amber-400 leading-relaxed mb-2.5">
              This plan carries an overall discount the server has not confirmed as applied, so the total cannot be
              shown. Both readings of it are wrong in a different direction — guessing here would be a wrong number
              about money.
            </p>
          )}
          <div className="flex justify-between text-[12px] py-1.5 text-[#a1a1aa]">
            <span>Catalogue list</span><b className="text-white tabular-nums">{money2(listCents)}</b>
          </div>
          {saved > 0 && (
            <div className="flex justify-between text-[12px] py-1.5" style={{ color: '#4ade80' }}>
              <span>{code ? `${code} · deal price` : 'Deal price'}</span>
              <b className="tabular-nums">−{money2(saved)}</b>
            </div>
          )}
          <div className="flex justify-between text-[13.5px] pt-2.5 mt-1.5 border-t border-white/10 text-[#a1a1aa]">
            <span>Billing now</span><b className="text-white tabular-nums">{money2(netCents)} /mo</b>
          </div>

          <div className="h-px bg-white/[.08] my-3.5" />

          {/* ── WHERE THIS PRICE CAME FROM ── */}
          <p className="text-[9.5px] font-mono uppercase tracking-[0.16em] font-extrabold text-[#6b6b74] mb-2.5">
            Where this price came from
          </p>

          {code ? (
            <>
              <div className="flex items-center gap-2.5 mb-3 flex-wrap">
                <span className="inline-flex items-center gap-2 px-[11px] py-[5px] rounded-full text-[11px] font-extrabold border"
                  style={{ background: 'rgba(220,20,60,.14)', borderColor: 'rgba(220,20,60,.3)', color: '#fda4af' }}>
                  <Tag size={11} /> {code}
                </span>
                {dealName && <span className="text-[11.5px] text-[#6b6b74]">{dealName}</span>}
              </div>

              {partner ? (
                <div className="flex gap-3 items-center p-3 rounded-xl bg-white/[.04] border border-white/[.08] flex-wrap">
                  <span className="w-[38px] h-[38px] rounded-[11px] flex-none grid place-items-center font-black text-[13px] text-white"
                    style={{ background: `linear-gradient(135deg,#e8365a,${RUBY_DEEP})` }}>
                    {(partner.name || '?').slice(0, 2).toUpperCase()}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[13.5px] font-extrabold text-white truncate">{partner.name}</span>
                    <span className="block text-[11px] text-[#6b6b74] truncate">
                      {[partner.company, partner.status, partner.referralCode].filter(Boolean).join(' · ')}
                    </span>
                    <span className="block text-[11px] text-[#6b6b74] mt-0.5">
                      {partner.commissionMode === 'percentage' ? `${partner.percentageRate}% recurring`
                        : partner.commissionMode === 'flat' ? `${money2(partner.flatFee)} flat`
                        : `${partner.percentageRate}% recurring + ${money2(partner.flatFee)} flat`}
                      {typeof partner.referralCount === 'number' ? ` · ${partner.referralCount} referrals` : ''}
                      {typeof partner.conversionCount === 'number' ? ` · ${partner.conversionCount} converted` : ''}
                    </span>
                  </span>
                  <Link
                    to={`/affiliates/${partner.id}`}
                    className="flex-none px-[11px] py-1.5 rounded-[9px] text-[11px] font-extrabold border border-white/[.14] bg-white/[.05] text-[#e4e4e7] hover:bg-white/10"
                  >
                    Partner profile
                  </Link>
                </div>
              ) : (
                /*
                  A DEAL WITH NO AFFILIATE IS A HOUSE OFFER, not a broken
                  record. Saying "did not resolve" for both cases would send
                  somebody hunting for a partner who was never meant to exist,
                  and would hide the case that IS worth chasing: a code naming
                  no deal at all.
                */
                <p className="text-[11.5px] text-[#6b6b74] leading-relaxed">
                  {dealFound
                    ? 'House offer — sold directly, with no partner attached and no commission payable on it.'
                    : 'This code does not name any deal we hold. Worth checking before anyone is paid on it, and before it is quoted back to the customer.'}
                </p>
              )}

              {/*
                IS EVERYONE PAID CORRECTLY. Read from the commission ledger,
                never recomputed here: a second opinion about what a partner is
                owed is exactly the kind of thing that ends in an argument.
              */}
              {partner && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-[7px] mt-2.5">
                    {([
                      ['Accrued', partner.totalEarnings, false],
                      ['Clearing', partner.clearingEarnings, true],
                      ['Available', partner.availableEarnings, false],
                      ['Paid', partner.paidEarnings, false],
                    ] as const).map(([label, amount, warm]) => (
                      <div key={label} className="px-2.5 py-2 rounded-[10px] text-center border"
                        style={warm
                          ? { background: 'rgba(245,158,11,.12)', borderColor: 'rgba(245,158,11,.35)' }
                          : { background: 'rgba(255,255,255,.03)', borderColor: 'rgba(255,255,255,.07)' }}>
                        <div className="text-[9.5px] uppercase tracking-[0.1em] font-extrabold"
                          style={{ color: warm ? '#fbbf24' : '#6b6b74' }}>{label}</div>
                        <div className="text-[12.5px] font-extrabold mt-[3px] tabular-nums"
                          style={{ color: warm ? '#fbbf24' : '#e4e4e7' }}>{money2(amount)}</div>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-[#6b6b74] leading-relaxed mt-2.5">
                    Commission accrues when the order is placed and <b className="text-[#a1a1aa]">clears after the money
                    settles</b> — so a refund inside that window forfeits it rather than clawing it back from a partner
                    who has already been paid.
                  </p>
                </>
              )}
            </>
          ) : (
            <>
              <span className="inline-flex items-center gap-2 px-[11px] py-[5px] rounded-full text-[11px] font-extrabold border border-white/[.12] bg-white/[.05] text-[#a1a1aa]">
                Direct sale
              </span>
              <p className="text-[11px] text-[#6b6b74] leading-relaxed mt-2.5">
                Catalogue pricing, no partner attached, no commission payable. Said out loud rather than left blank:
                a missing deal code and an unattributed sale look identical when the row is simply absent — and one of
                those is a partner not getting paid.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
};

/* ── what they pay over the next six months ────────────────────────────── */

const MONTHS_AHEAD = 12;

/*
  ══════════════════════════════════════════════════════════════════════════
  THE SCHEDULE IS BUILT FROM WHAT WE SENT STRIPE, NOT FROM A RE-DERIVATION
  ══════════════════════════════════════════════════════════════════════════

  This panel had three independent faults and produced "$136.98 due today,
  then five months of $0.00" for an account that had just paid $700.40 and
  bills $250.41 a month.

    · Software came from `row?.totals?.netCents ?? row?.lineNetCents`. NEITHER
      field exists on `PlanAssignmentRow` — it carries package ids and
      discounts and no money at all — so software was `undefined`, then `0`,
      in every row. (The same dead read is documented in PaymentVerifyPanel.)
    · Devices came from `units[].agreement`, and units exist only once an order
      is ALLOCATED. Payment is verified long before that, so the list was empty.
    · `dueCents` was handed the hardware MONTHLY and printed it as "due today".

  Everything needed was already recorded. The completion stores what we sent
  Stripe, line by line, with each device's own term; the pinned deal version
  stores the promotions we applied. Both are read here, so this schedule is the
  sale itself rather than a guess reassembled from whatever was to hand.
*/

export const PaymentBreakdown: React.FC<{
  tenantId: string;
  units: DeviceUnitRow[];
  /** DEPRECATED as an input — see the note above. Kept so callers need not change. */
  dueCents?: number;
}> = ({ tenantId }) => {
  const [sold, setSold] = useState<SoldLocation[] | null>(null);
  const [intros, setIntros] = useState<Record<string, { introPriceCents: number; introMonths: number }>>({});
  const [packageIdBySku, setPackageIdBySku] = useState<Record<string, string>>({});
  const [dealVersionNo, setDealVersionNo] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const dataVersion = useDataVersion();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [result, tenant, skus] = await Promise.all([
        billingApi.getOnboardingResult(tenantId).catch(() => null),
        api.superAdmin.getTenant(tenantId).catch(() => null),
        billingApi.listSkus().catch(() => null),
      ]);
      if (cancelled) return;
      setSold((result as any)?.result?.locations || []);
      const byS: Record<string, string> = {};
      for (const s of (skus as any)?.items || []) if (s.packageId) byS[s.skuId] = s.packageId;
      setPackageIdBySku(byS);

      // THE PINNED VERSION, never the live deal: the version the tenant signed
      // is the contract, and the live document may since have been edited to
      // say something else entirely.
      const dealId = (tenant as any)?.dealId;
      const versionNo = (tenant as any)?.dealVersionNo;
      if (dealId && typeof versionNo === 'number') {
        try {
          const v: any = await dealsApi.getVersion(dealId, versionNo);
          if (!cancelled) {
            setIntros((v?.version || v)?.packageIntros || {});
            setDealVersionNo(versionNo);
          }
        } catch { /* no promotions shown rather than wrong ones */ }
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  // dataVersion: re-read in place after a save anywhere on the order screen.
  }, [tenantId, dataVersion]);

  /** Every line we sent, across every location on this account. */
  const lines = (sold || []).flatMap((l) => l.lines || []);
  const oneTimeCents = (sold || [])
    .flatMap((l) => l.oneTimeLines || [])
    .reduce((n, l) => n + l.amountCents * (l.quantity || 1), 0);
  const hasSale = lines.length > 0 || oneTimeCents > 0;

  const isDevice = (l: { kind?: string }) => l.kind === 'device';

  const now = new Date();
  const rows = Array.from({ length: MONTHS_AHEAD }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);

    /*
      SOFTWARE, with each package's own promotion applied for exactly as long
      as the deal promised it. `introPriceCents` is the amount that package
      bills DURING the promotion, so the month's charge is the intro price
      while it runs and the settled price afterwards.
    */
    let softwareCents = 0;
    let softwareListCents = 0;
    for (const l of lines.filter((x) => !isDevice(x))) {
      const qty = l.quantity || 1;
      const intro = intros[packageIdBySku[l.skuId] || ''];
      const unit = intro && i < intro.introMonths ? intro.introPriceCents : l.amountCents;
      softwareCents += unit * qty;
      softwareListCents += l.amountCents * qty;
    }
    /*
      THE OFFER, AS ITS OWN NUMBER.

      Kept separate rather than folded into the software figure: "software
      $113.43" invites the question the whole panel exists to answer, and the
      month a promotion ENDS is the one an operator most needs to see coming.
    */
    const discountCents = softwareListCents - softwareCents;

    // A device on a 24-month term stops billing in month 25. Carrying it for
    // ever overstates the commitment, which is the figure being checked here.
    const deviceCents = lines
      .filter(isDevice)
      .filter((l) => l.termMonths === undefined || i < l.termMonths)
      .reduce((n, l) => n + l.amountCents * (l.quantity || 1), 0);

    // Deposits and down payments are taken once, with the first month.
    const oneTime = i === 0 ? oneTimeCents : 0;
    return {
      label: d.toLocaleString(undefined, { month: 'short', year: 'numeric' }),
      softwareCents,
      softwareListCents,
      discountCents,
      deviceCents,
      oneTime,
      total: softwareCents + deviceCents + oneTime,
    };
  });

  if (loading) {
    return <RubyLoader variant="inline" label="building the schedule…" />;
  }

  /*
    NO PLAN, NO SCHEDULE. Six rows of $0.00 look like a subscription that costs
    nothing rather than one that has not been set up — the opposite of what an
    operator needs to notice before verifying a payment.
  */
  /*
    NO RECORD, NO SCHEDULE. Twelve rows of $0.00 look like a subscription that
    costs nothing rather than one whose sale we cannot find — the opposite of
    what an operator needs to notice before verifying a payment.
  */
  if (!hasSale) {
    return (
      <p className="text-[12px] text-amber-700 leading-relaxed">
        We have no completed sale recorded for this tenant, so there is nothing to project.
        If they have paid, that is worth resolving before this payment is verified.
      </p>
    );
  }

  return (
    <div>
      {/*
        WHAT WAS TAKEN TODAY, said first and said plainly.

        The panel opened with "Nothing collected yet" — a fixed string, on an
        order that had already collected $700.40. The first month and the
        one-time charges are taken together at checkout, so they are stated
        together here.
      */}
      <p className="text-[12.5px] text-stone-700 mb-2">
        <b>{money(rows[0].total)}</b> taken today
        <span className="text-stone-400">
          {' — '}first month {money(rows[0].softwareCents + rows[0].deviceCents)}
          {rows[0].oneTime > 0 && <> · deposits &amp; down payments {money(rows[0].oneTime)}</>}
        </span>
      </p>

      <p className="text-[11px] uppercase tracking-[0.12em] font-bold text-stone-500 mb-2 inline-flex items-center gap-1.5">
        <CalendarClock size={12} /> Next {MONTHS_AHEAD} months
      </p>

      <div className="rounded-xl border border-stone-200 overflow-hidden">
        {rows.map((r, i) => {
          /*
            THE ARITHMETIC, WRITTEN OUT — software − offers + hardware = total.

            The row used to print figures side by side with no operator between
            them, so a reader checking $250.41 against a bank statement had to
            work out for themselves which parts made it up. The offer is named
            in its own colour because the month it disappears is the month the
            bill jumps, and that is the thing worth seeing early.
          */
          const promoEnded = i > 0 && rows[i - 1].discountCents > 0 && r.discountCents === 0;
          return (
            <div
              key={r.label}
              className={`flex items-center gap-3 px-3 py-2 ${i % 2 ? 'bg-stone-50' : 'bg-white'} ${i ? 'border-t border-stone-100' : ''}`}
            >
              <span className="text-[12px] text-stone-500 w-[74px] shrink-0">{r.label}</span>
              <span className="flex-1 min-w-0 text-[11px] text-stone-400">
                {r.total === 0 ? 'nothing due' : (
                  <>
                    <span className="text-stone-500">{money(r.softwareListCents)}</span> software
                    {r.discountCents > 0 && (
                      <span className="text-emerald-600 font-semibold"> − {money(r.discountCents)} offers</span>
                    )}
                    {r.deviceCents > 0 && (
                      <> + <span className="text-stone-500">{money(r.deviceCents)}</span> hardware</>
                    )}
                    {r.oneTime > 0 && (
                      <> + <span className="text-stone-700 font-semibold">{money(r.oneTime)}</span> deposits &amp; down payments</>
                    )}
                    {promoEnded && (
                      <span className="text-amber-700 font-semibold"> · promotion ended</span>
                    )}
                  </>
                )}
              </span>
              <span className={`text-[12.5px] font-semibold shrink-0 ${i === 0 ? 'text-stone-900' : 'text-stone-700'}`}>
                {money(r.total)}
              </span>
            </div>
          );
        })}
      </div>

      <p className="text-[11px] text-stone-400 mt-2 leading-relaxed">
        Built from what we sent Stripe at checkout
        {dealVersionNo ? <> and the offer on <b>deal v{dealVersionNo}</b></> : null} — not an invoice.
        A device&apos;s rows stop when its term ends, and each package returns to its full price when its
        promotion runs out.
      </p>
    </div>
  );
};
